import React, { useState, useRef } from 'react';
import Modal, { MODAL_WELL, MODAL_ERROR, MODAL_NOTE } from './Modal';
import Button from './Button';
import CardPaymentSection from './CardPaymentSection';
import DevCardElement from './DevCardElement';
import { proService, stripeService } from '../services';
import { isStripeDevMode, mockConfirmCardSetup } from '../utils/stripeDevMode';
import { SPECIES } from '../theme/species';

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

/**
 * ProSubscribeModal — start the monthly Pro subscription (6e). Collects a card via
 * the shared SetupIntent + CardPaymentSection flow, then creates the Stripe
 * subscription. Only reached by standard-tier Pros (founding / charity are free).
 */
export default function ProSubscribeModal({ isOpen, onClose, monthlyPriceCents, email, name, onDone }) {
  const pink = SPECIES.partyStarter;
  const cardRef = useRef(null);
  const [cardReady, setCardReady] = useState(false);
  const [devBehavior, setDevBehavior] = useState('success');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const close = () => { if (!submitting) { setError(''); onClose(); } };

  const submit = async () => {
    setSubmitting(true); setError('');
    try {
      let pmId = null;
      let customerId = null;
      if (isStripeDevMode()) {
        const mock = await mockConfirmCardSetup(devBehavior);
        if (mock.error) { setError(mock.error.message); setSubmitting(false); return; }
        pmId = mock.setupIntent.payment_method;
        customerId = `cus_dev_${Date.now()}`;
      } else {
        const setup = await stripeService.createSetupIntent();
        if (!setup.success) { setError(setup.error || 'Could not prepare payment.'); setSubmitting(false); return; }
        if (!cardRef.current) { setError('Card form is not ready.'); setSubmitting(false); return; }
        const res = await cardRef.current.confirmSetup(setup.data.clientSecret, { email, name });
        if (res.error) { setError(res.error); setSubmitting(false); return; }
        pmId = res.paymentMethod;
        customerId = setup.data.customerId;
      }
      const r = await proService.startSubscription({ stripePaymentMethodId: pmId, stripeCustomerId: customerId });
      if (r.success) { onDone?.(r.data); onClose(); }
      else setError(r.error || 'Could not start your subscription.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={close} title="Pro subscription" size="md">
      <div className="space-y-4">
        <div className={`flex items-end justify-between ${MODAL_WELL}`}>
          <span className="font-mono text-xs font-bold uppercase tracking-wider">Portal Pro</span>
          <span className="font-mono text-lg font-bold">{money(monthlyPriceCents)}<span className="text-xs text-gray-500"> / mo</span></span>
        </div>
        <p className={MODAL_NOTE}>
          Keeps your Pro page live and bookable. Cancel anytime — you stay active through the period you’ve paid for.
        </p>
        <div className={MODAL_WELL}>
          {isStripeDevMode()
            ? <DevCardElement value={devBehavior} onChange={setDevBehavior} />
            : <CardPaymentSection ref={cardRef} amountCents={monthlyPriceCents} currency="cad" countryDefault="CA" onValidityChange={setCardReady} disabled={submitting} label="PAY WITH CARD" />}
        </div>
        {error && <div className={MODAL_ERROR} role="alert">{error}</div>}
        <Button type="button" fullWidth onClick={submit} disabled={submitting || (!isStripeDevMode() && !cardReady)} style={{ background: pink, color: '#000' }}>
          {submitting ? 'Starting…' : `Subscribe · ${money(monthlyPriceCents)}/mo`}
        </Button>
      </div>
    </Modal>
  );
}
